// src/components/layout/Sidebar.jsx
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '../../store/uiStore';
import { useAuth }   from '../../hooks/useAuth';
import { C }         from '../../styles/tokens';
import { initials }  from '../../utils/formatters';

const navSections = [
  { label: 'MAIN', items: [
    { icon: '⊞', label: 'Dashboard',       path: '/dashboard' },
    { icon: '🔍', label: 'Opportunities',   path: '/opportunities' },
    { icon: '📄', label: 'My Applications', path: '/applications', badge: 3 },
    { icon: '🤖', label: 'AI CV Optimizer', path: '/cv-optimizer' },
  ]},
  { label: 'FINANCE', items: [
    { icon: '🪙', label: 'ONT Wallet',   path: '/wallet', badge: 'NEW' },
    { icon: '🎁', label: 'Referrals',    path: '/referral' },
  ]},
  { label: 'LEARN', items: [
    { icon: '🎓', label: 'Training Hub', path: '/training' },
    { icon: '🏅', label: 'Certificates', path: '/certs' },
  ]},
  { label: 'ACCOUNT', items: [
    { icon: '👤', label: 'Profile',      path: '/profile' },
    { icon: '⚙️', label: 'Settings',     path: '/settings' },
  ]},
];

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const col = sidebarCollapsed;
  const sw  = col ? 68 : 240;

  const isActive = (path) => location.pathname === path;

  return (
    <aside style={{
      width:          sw,
      height:         'calc(100vh - 64px)',
      position:       'fixed',
      top:            '64px',
      left:           0,
      zIndex:         40,
      background:     C.surface1,
      borderRight:    '1px solid rgba(254,52,232,0.08)',
      display:        'flex',
      flexDirection:  'column',
      transition:     'width 0.3s cubic-bezier(0.16,1,0.3,1)',
      overflow:       'hidden',
    }}>
      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 10px' }}>
        {navSections.map(sec => (
          <div key={sec.label} style={{ marginBottom: '20px' }}>
            {!col && (
              <div style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em', padding: '0 5px', marginBottom: '5px' }}>
                {sec.label}
              </div>
            )}
            {sec.items.map(item => {
              const active = isActive(item.path);
              return (
                <div
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    display:        'flex',
                    alignItems:     'center',
                    gap:            '10px',
                    padding:        col ? '10px' : '9px 12px',
                    borderRadius:   '9px',
                    cursor:         'pointer',
                    fontSize:       '13.5px',
                    fontWeight:     500,
                    marginBottom:   '2px',
                    justifyContent: col ? 'center' : 'flex-start',
                    transition:     'all 0.2s',
                    position:       'relative',
                    background: active ? 'linear-gradient(135deg,rgba(254,52,232,0.15),rgba(95,8,231,0.15))' : 'transparent',
                    border:     active ? '1px solid rgba(254,52,232,0.2)' : '1px solid transparent',
                    color:      active ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#fff'; }}}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}}
                >
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
                  {!col && <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.label}</span>}
                  {!col && item.badge && (
                    <span style={{
                      fontSize: '10px', padding: '2px 7px', borderRadius: '100px', fontWeight: 700,
                      background: typeof item.badge === 'number' ? 'rgba(254,52,232,0.2)' : 'rgba(0,208,132,0.2)',
                      color:      typeof item.badge === 'number' ? C.magenta : C.success,
                    }}>
                      {item.badge}
                    </span>
                  )}
                  {col && item.badge && (
                    <div style={{ position: 'absolute', top: '6px', right: '6px', width: '7px', height: '7px', borderRadius: '50%', background: C.magenta }} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User card */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(254,52,232,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0, fontFamily: "'Syne',sans-serif" }}>
            {initials(user?.displayName || 'U')}
          </div>
          {!col && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.displayName || 'User'}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.38)' }}>Verified ✅</div>
            </div>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        style={{ position: 'absolute', top: '20px', right: '-12px', width: '24px', height: '24px', borderRadius: '50%', background: C.surface2, border: '1px solid rgba(254,52,232,0.2)', color: 'white', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
      >
        {col ? '›' : '‹'}
      </button>
    </aside>
  );
}
